import React, { useEffect, useMemo, useState } from "react";
import { PayPalScriptProvider, PayPalButtons } from "@paypal/react-paypal-js";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Heart, Loader2, CheckCircle2 } from "lucide-react";

const SYMBOL = { USD: "$", GBP: "£", EUR: "€", CAD: "CA$" };
const PAYPAL_BTN_STYLE = { layout: "vertical", color: "gold", shape: "pill", label: "donate" };

export function SupportDialog({ open, onOpenChange }) {
  const [config, setConfig] = useState(null);
  const [currency, setCurrency] = useState("USD");
  const [amount, setAmount] = useState(15);
  const [custom, setCustom] = useState("");
  const [name, setName] = useState("");
  const [done, setDone] = useState(null); // {amount, currency}

  useEffect(() => {
    if (open && !config) api.getPaymentConfig().then(setConfig).catch(() => setConfig({ enabled: false }));
  }, [open, config]);

  const value = useMemo(() => {
    const v = custom !== "" ? parseFloat(custom) : amount;
    return Number.isFinite(v) && v > 0 ? Math.round(v * 100) / 100 : 0;
  }, [custom, amount]);

  const close = (o) => { onOpenChange(o); if (!o) { setDone(null); setCustom(""); setAmount(15); setName(""); } };

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent className="max-w-md" data-testid="support-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-serif text-2xl text-[#082D4A]"><Heart size={20} className="fill-[#D4A017] text-[#D4A017]" /> Support {config?.site_name || "our family site"}</DialogTitle>
          <DialogDescription>Donations keep Where Am I From online and ad-free for the whole family. Chip in whatever you can to help keep the project going — thank you!</DialogDescription>
        </DialogHeader>

        {done ? (
          <div className="py-6 text-center" data-testid="support-success">
            <CheckCircle2 size={46} className="mx-auto mb-3 text-[#082D4A]" />
            <p className="font-serif text-xl text-[#082D4A]">Thank you{name ? `, ${name.split(" ")[0]}` : ""}!</p>
            <p className="mt-1 text-sm text-[#64748B]">Your {SYMBOL[done.currency] || ""}{done.amount} donation means the world to our family.</p>
          </div>
        ) : config && !config.enabled ? (
          <p className="py-6 text-center text-sm text-[#64748B]" data-testid="support-disabled">Donations aren't set up yet. Please check back soon.</p>
        ) : !config ? (
          <div className="flex justify-center py-10"><Loader2 className="animate-spin text-[#082D4A]" /></div>
        ) : (
          <div className="space-y-4">
            <div className="flex items-center justify-between gap-3">
              <Label className="text-xs uppercase tracking-wider text-[#64748B]">Currency</Label>
              <div className="flex gap-1" data-testid="currency-picker">
                {config.currencies.map((c) => (
                  <button key={c} onClick={() => setCurrency(c)} data-testid={`currency-${c}`}
                    className={`rounded-full px-2.5 py-1 text-xs font-semibold ${currency === c ? "bg-[#082D4A] text-white" : "bg-[#E8EEF3] text-[#082D4A]"}`}>{c}</button>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-4 gap-2">
              {config.presets.map((p) => (
                <button key={p} onClick={() => { setAmount(p); setCustom(""); }} data-testid={`amount-${p}`}
                  className={`rounded-xl border py-2 text-sm font-semibold ${custom === "" && amount === p ? "border-[#D4A017] bg-[#D4A017]/10 text-[#D4A017]" : "border-[#D8E0E8] text-[#082D4A] hover:border-[#D4A017]/40"}`}>
                  {SYMBOL[currency] || ""}{p}
                </button>
              ))}
            </div>

            <div className="space-y-1">
              <Label className="text-xs">Or a custom amount ({currency})</Label>
              <Input type="number" min="1" step="1" value={custom} onChange={(e) => setCustom(e.target.value)} placeholder="Enter amount" data-testid="custom-amount" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Your name (optional)</Label>
              <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="So we can say thanks" data-testid="donor-name" />
            </div>

            <div className="pt-1" data-testid="paypal-buttons">
              {value > 0 ? (
                <PayPalScriptProvider
                  key={currency}
                  options={{ "client-id": config.client_id, currency, intent: "capture", components: "buttons" }}
                >
                  <PayPalButtons
                    style={PAYPAL_BTN_STYLE}
                    forceReRender={[value, currency]}
                    createOrder={async () => {
                      const { order_id } = await api.createDonationOrder({ amount: value, currency, donor_name: name });
                      return order_id;
                    }}
                    onApprove={async (data) => {
                      try {
                        const res = await api.captureOrder(data.orderID);
                        setDone({ amount: res.amount, currency: res.currency });
                      } catch {
                        toast.error("We couldn't complete your donation. Please try again.");
                      }
                    }}
                    onError={() => toast.error("PayPal ran into a problem. Please try again.")}
                  />
                </PayPalScriptProvider>
              ) : (
                <p className="text-center text-xs text-[#94A3B8]">Choose or enter an amount to continue</p>
              )}
            </div>
            <p className="text-center text-[10px] text-[#94A3B8]">Secure one-time payment via PayPal · you can use any card</p>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
